import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { Slider } from 'primereact/slider';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import routeCodeServices from '../../services/route-codes/routecode.service';
import editUserServices from '../../services/edit-users/edit-users.service';
import AuthService from '../../services/autentication/auth.service';
import orderServices from '../../services/orders/orderservice';

interface CustomerFollowUpSearchProps {
    onSearch: (filters: {
        priority?: string | null;
        routeCodeName?: string | null;
        currentMonth?: string | null;
        showNullStatusOnly?: boolean;
        salesExecutiveID?: number | null;
        userID?: number | null;
        daysSinceRecentOrderMin?: number | null;
        daysSinceRecentOrderMax?: number | null;
        recentOrderAmountMin?: number | null;
        recentOrderAmountMax?: number | null;
        lastCalledStartDate?: string | null;
        lastCalledEndDate?: string | null;
    }) => void;
    onReset: () => void;
    maxRecentOrderAmount: number;
}

interface RouteCode {
    routeCodeID: number;
    name: string;
}

interface CallStatus {
    callStatusId: number;
    callStatusName: string;
}

interface SalesExecutive {
    salesExecutiveId: number;
    salesExecutiveName: string;
}

interface UserOption {
    userID: number;
    name: string;
    primaryContactNbr: string;
}

interface UserResult {
    userID: number;
    detail: string;
}

const CustomerFollowUpSearch: React.FC<CustomerFollowUpSearchProps> = ({ 
    onSearch, 
    onReset,
    maxRecentOrderAmount = 10000
}) => {
    const toast = React.useRef<Toast>(null);
    const [priority, setPriority] = useState<string | null>(null);
    const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
    const [selectedCallStatus, setSelectedCallStatus] = useState<string | null>(null);
    const [selectedExecutive, setSelectedExecutive] = useState<string | null>(null);
    const [routeOptions, setRouteOptions] = useState<{label: string, value: string}[]>([]);
    const [callStatusOptions, setCallStatusOptions] = useState<{label: string, value: string}[]>([]);
    const [executiveOptions, setExecutiveOptions] = useState<{label: string, value: string}[]>([]);
    const [loadingRoutes, setLoadingRoutes] = useState<boolean>(false);
    const [loadingCallStatuses, setLoadingCallStatuses] = useState<boolean>(false);
    const [loadingExecutives, setLoadingExecutives] = useState<boolean>(false);
    const blankUserDetail: UserOption[] = [{ userID: 0, name: '', primaryContactNbr: '' }];
    const [orderUsers, setOrderUsers] = useState<UserOption[]>(blankUserDetail);
    const [orderUser, setOrderUser] = useState<UserResult[]>([]);
    const [dropdownValue, setDropdownValue] = useState<any>();
    const [daysSinceRecentOrderRange, setDaysSinceRecentOrderRange] = useState<[number, number]>([0, 365]);
    const [recentOrderAmountRange, setRecentOrderAmountRange] = useState<[number, number]>([0, maxRecentOrderAmount]);
    const [lastCalledStartDate, setLastCalledStartDate] = useState<Date | null>(null);
    const [lastCalledEndDate, setLastCalledEndDate] = useState<Date | null>(null);

    useEffect(() => {
        setRecentOrderAmountRange([0, maxRecentOrderAmount]);
    }, [maxRecentOrderAmount]);

    const isAdminUser = (): boolean => {
        if (AuthService.isUserLoggedIn()) {
            const localData = localStorage.getItem("user");
            const parsedData = localData ? JSON.parse(localData) : null;
            return parsedData?.roleID === 2;
        }
        return false;
    };

    const handleDropdownChange = (e: any) => {
        const selectedUserId = e.value;
        const selectedUser = orderUser.find(user => user.userID === selectedUserId);
        
        if (selectedUser) {
            setDropdownValue(selectedUserId);
        }
    };

    const handleDropdownFilter = (e: any) => {
        if (e.filter.length > 2) {
            orderServices.getOrderUsers(e.filter).then((response: UserOption[]) => {
                if (response.length > 0) {
                    setOrderUsers(response);
                }
            });
        }
    };

    useEffect(() => {
        setOrderUser(orderUsers.map((user) => ({
            userID: user.userID,
            detail: user.userID !== 0 ? `${user.name}, ${user.primaryContactNbr}` : "",
        })));
    }, [orderUsers]);

    const formatDateForBackend = (date: Date | null): string | null => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`; // yyyy-MM-dd format
    };

    const handleSearch = () => {
        // Validate date range
        if ((lastCalledStartDate && !lastCalledEndDate) || (!lastCalledStartDate && lastCalledEndDate)) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please provide both start and end dates for the date range',
                life: 3000
            });
            return;
        }

        const isFollowUpNeeded = selectedCallStatus === 'followup_needed';
        const callStatusName = isFollowUpNeeded 
            ? null 
            : (selectedCallStatus 
                ? callStatusOptions.find(status => status.value === selectedCallStatus && status.value !== 'followup_needed')?.label ?? null
                : null);

        onSearch({
            priority,
            routeCodeName: selectedRoutes.length > 0 ? selectedRoutes.join(',') : null,
            currentMonth: callStatusName,
            showNullStatusOnly: isFollowUpNeeded,
            salesExecutiveID: selectedExecutive ? parseInt(selectedExecutive) : null,
            userID: dropdownValue ? dropdownValue : null,
            daysSinceRecentOrderMin: daysSinceRecentOrderRange[0] === 0 ? null : daysSinceRecentOrderRange[0],
            daysSinceRecentOrderMax: daysSinceRecentOrderRange[1] === 365 ? null : daysSinceRecentOrderRange[1],
            recentOrderAmountMin: recentOrderAmountRange[0] === 0 ? null : recentOrderAmountRange[0],
            recentOrderAmountMax: recentOrderAmountRange[1] === maxRecentOrderAmount ? null : recentOrderAmountRange[1],
            lastCalledStartDate: formatDateForBackend(lastCalledStartDate),
            lastCalledEndDate: formatDateForBackend(lastCalledEndDate)
        });
    };

    const handleReset = () => {
        setPriority(null);
        setSelectedRoutes([]);
        setSelectedCallStatus(null);
        setSelectedExecutive(null);
        setDropdownValue(null);
        setOrderUsers(blankUserDetail);
        setDaysSinceRecentOrderRange([0, 365]);
        setRecentOrderAmountRange([0, maxRecentOrderAmount]);
        setLastCalledStartDate(null);
        setLastCalledEndDate(null);
        onReset();
    };

    useEffect(() => {
        const fetchRouteCodes = async () => {
            setLoadingRoutes(true);
            try {
                const routeCodes = await routeCodeServices.getRouteCodes();
                if (routeCodes) {
                    const formattedRoutes = routeCodes.map((route: RouteCode) => ({
                        label: route.name || `Route ${route.routeCodeID}`,
                        value: route.name || route.routeCodeID.toString()
                    }));
                    setRouteOptions(formattedRoutes);
                }
            } catch (error) {
                console.error('Error fetching route codes:', error);
                setRouteOptions([]);
            } finally {
                setLoadingRoutes(false);
            }
        };

        const fetchCallStatuses = async () => {
            setLoadingCallStatuses(true);
            try {
                const callStatuses = await editUserServices.getCallStatuses();
                const formattedStatuses = [
                    { 
                        label: 'Follow up needed', 
                        value: 'followup_needed' 
                    },
                    ...(callStatuses?.map((status: CallStatus) => ({
                        label: status.callStatusName,
                        value: status.callStatusId.toString()
                    })) || [])
                ];
                setCallStatusOptions(formattedStatuses);
            } catch (error) {
                console.error('Error fetching call statuses:', error);
                setCallStatusOptions([{ 
                    label: 'Follow up needed', 
                    value: 'followup_needed' 
                }]);
            } finally {
                setLoadingCallStatuses(false);
            }
        };

        const fetchDeliveryExecutives = async () => {
            if (isAdminUser()) {
                setLoadingExecutives(true);
                try {
                    const executives = await editUserServices.getDeliveryExecutives();
                    if (executives) {
                        const formattedExecutives = executives.map((exec: SalesExecutive) => ({
                            label: exec.salesExecutiveName,
                            value: exec.salesExecutiveId.toString()
                        }));
                        setExecutiveOptions(formattedExecutives);
                    }
                } catch (error) {
                    console.error('Error fetching delivery executives:', error);
                    setExecutiveOptions([]);
                } finally {
                    setLoadingExecutives(false);
                }
            }
        };

        fetchRouteCodes();
        fetchCallStatuses();
        fetchDeliveryExecutives();
    }, []);

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <div className="card border-noround p-3">
                <div className="flex flex-wrap align-items-center gap-3">
                    <div className="flex align-items-center gap-2" style={{ flex: '1 1 20%' }}>
                        <label className="font-bold mr-2">User:</label>
                        <Dropdown
                            options={orderUser || []}
                            optionLabel="detail"
                            optionValue="userID"
                            value={dropdownValue}
                            onChange={handleDropdownChange}
                            onFilter={handleDropdownFilter}
                            filter
                            placeholder="Search for User/Mobile"
                            style={{ width: '100%' }}
                        />
                    </div>

                    <Divider layout="vertical" />

                    <div className="flex align-items-center gap-2" style={{ flex: '1 1 20%' }}>
                        <label className="font-bold mr-2">Priority:</label>
                        <div className="flex align-items-center gap-2">
                            <Checkbox 
                                inputId="highPriority" 
                                checked={priority === 'High'}
                                onChange={() => setPriority(priority === 'High' ? null : 'High')}
                            />
                            <label htmlFor="highPriority">High</label>
                        </div>
                        <div className="flex align-items-center gap-2">
                            <Checkbox 
                                inputId="mediumPriority" 
                                checked={priority === 'Medium'}
                                onChange={() => setPriority(priority === 'Medium' ? null : 'Medium')}
                            />
                            <label htmlFor="mediumPriority">Medium</label>
                        </div>
                        <div className="flex align-items-center gap-2">
                            <Checkbox 
                                inputId="lowPriority" 
                                checked={priority === 'Low'}
                                onChange={() => setPriority(priority === 'Low' ? null : 'Low')}
                            />
                            <label htmlFor="lowPriority">Low</label>
                        </div>
                    </div>

                    <Divider layout="vertical" />

                    <div className="flex align-items-center gap-2" style={{ flex: '1 1 15%' }}>
                        <label className="font-bold mr-2">Route-code:</label>
                        <MultiSelect
                            value={selectedRoutes}
                            options={routeOptions}
                            onChange={(e) => setSelectedRoutes(e.value)}
                            optionLabel="label"
                            placeholder="Select Routes"
                            display="chip"
                            maxSelectedLabels={3}
                            selectedItemsLabel="{0} routes selected"
                            style={{ width: '100%' }}
                            className="multiselect-custom"
                        />
                    </div>

                    <Divider layout="vertical" />

                    <div className="flex align-items-center gap-2" style={{ flex: '1 1 20%' }}>
                        <label className="font-bold mr-2">Call Status:</label>
                        <Dropdown
                            value={selectedCallStatus}
                            options={callStatusOptions}
                            onChange={(e) => setSelectedCallStatus(e.value)}
                            placeholder={"Select Status"}
                            style={{ width: '80%' }}
                        />
                    </div>

                    {isAdminUser() && (
                        <>
                            <Divider layout="vertical" />
                            <div className="flex align-items-center gap-2" style={{ flex: '1 1 55%' }}>
                                <label className="font-bold mr-2">Customer Executive:</label>
                                <Dropdown
                                    value={selectedExecutive}
                                    options={executiveOptions}
                                    onChange={(e) => setSelectedExecutive(e.value)}
                                    placeholder={"Select Executive"}
                                    style={{ width: '35%' }}
                                />
                            </div>
                        </>
                    )}
                </div>

                <Divider />

                <div className="flex flex-wrap align-items-center gap-3">
                    <div className="flex flex-column gap-2" style={{ flex: '1 1 20%' }}>
                        <label className="font-bold mb-1">
                            Days Since Last Ordered: {daysSinceRecentOrderRange[0]} - {daysSinceRecentOrderRange[1]} days
                        </label>
                        <Slider 
                            value={daysSinceRecentOrderRange}
                            onChange={(e) => setDaysSinceRecentOrderRange(e.value as [number, number])}
                            range 
                            min={0} 
                            max={365}
                            className="w-14rem"
                            style={{ 
                                '--slider-handle-width': '12px',
                                '--slider-handle-height': '12px',
                                '--slider-height': '4px'
                            } as React.CSSProperties}
                        />
                    </div>

                    <Divider layout="vertical" />

                    <div className="flex flex-column gap-2" style={{ flex: '1 1 25%' }}>
                        <label className="font-bold mb-1">
                            Last Purchase Amount: ₹{recentOrderAmountRange[0]} - ₹{recentOrderAmountRange[1]}
                            {maxRecentOrderAmount > 10000 && ` (Max: ₹${maxRecentOrderAmount})`}
                        </label>
                        <Slider 
                            value={recentOrderAmountRange}
                            onChange={(e) => setRecentOrderAmountRange(e.value as [number, number])}
                            range 
                            min={0} 
                            max={maxRecentOrderAmount}
                            step={100}
                            className="w-14rem"
                            style={{ 
                                '--slider-handle-width': '15px',
                                '--slider-handle-height': '12px',
                                '--slider-height': '4px'
                            } as React.CSSProperties}
                        />
                    </div>

                    <Divider layout="vertical" />

                    <div className="flex flex-column gap-2" style={{ flex: '1 1 25%' }}>
                        <label className="font-bold mb-1">Last Called Date Range</label>
                        <div className="flex align-items-center gap-2">
                            <Calendar
                                value={lastCalledStartDate}
                                onChange={(e) => setLastCalledStartDate(e.value as Date)}
                                placeholder="Start Date"
                                dateFormat="dd/mm/yy"
                                className="w-full"
                            />
                            <Calendar
                                value={lastCalledEndDate}
                                onChange={(e) => setLastCalledEndDate(e.value as Date)}
                                placeholder="End Date"
                                dateFormat="dd/mm/yy"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 justify-content-end ml-auto" style={{ flex: '1 1 20%', minWidth: '200px' }}>
                        <Button
                            className="text-black border-round p-button-m"
                            severity="success"
                            raised
                            label="Filter"
                            style={{ width: "100px" }}
                            onClick={handleSearch}
                        />
                        <Button
                            className="text-black border-round p-button-m"
                            severity="secondary"
                            text
                            raised
                            label="Reset"
                            style={{ width: "100px" }}
                            onClick={handleReset}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerFollowUpSearch;